import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_PRE = [
  { label: "About", href: "#about" },
];
const NAV_POST = [
  { label: "Features", href: "#features" },
  { label: "Why Fertilizer Tracking", href: "#why" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    to: "/login",
    icon: "fa-box",
    title: "Fertilizer Bag Tracking",
    sub: "QR verify · subsidy · fraud AI",
    cls: "svc-fert",
  },
  {
    to: "/fleet/login",
    icon: "fa-truck-fast",
    title: "Fleet GPS Tracking",
    sub: "Live map · trips · alerts",
    cls: "svc-fleet",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false); // mobile drawer
  const [svcOpen, setSvcOpen] = useState(false); // services dropdown
  const svcRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close dropdown on outside click / Esc
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (svcRef.current && !svcRef.current.contains(e.target as Node)) setSvcOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSvcOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const isLanding = pathname === "/";
  const closeAll = () => {
    setSvcOpen(false);
    setOpen(false);
  };
  const goHome = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    closeAll();
  };

  return (
    <nav className={`nav ${scrolled ? "scrolled" : ""}`} data-testid="main-navbar">
      <div className="nav-inner">
        <Link to="/" className="brand" data-testid="nav-brand">
          <div className="brand-icon">U</div>
          <div className="brand-text">
            <h1>Fertilizer Tracking</h1>
            <span>Smart Fertilizer Tracking</span>
          </div>
        </Link>

        <div className={`nav-links ${open ? "open" : ""}`}>
          {isLanding && (
            <>
              <a href="#top" onClick={goHome} data-testid="nav-link-home">
                Home
              </a>
              {NAV_PRE.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={closeAll}
                  data-testid={`nav-link-${l.href.slice(1)}`}
                >
                  {l.label}
                </a>
              ))}

              {/* Services → dropdown to select a portal */}
              <div className={`svc-dd ${svcOpen ? "open" : ""}`} ref={svcRef}>
                <button
                  type="button"
                  className={`svc-trigger ${svcOpen ? "active" : ""}`}
                  onClick={() => setSvcOpen((o) => !o)}
                  aria-expanded={svcOpen}
                  data-testid="nav-services-trigger"
                >
                  Services <i className="fa-solid fa-chevron-down"></i>
                </button>

                <div className="svc-menu" role="menu">
                  <div className="svc-menu-head">Select a portal to open</div>
                  {SERVICES.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      className={`svc-item ${s.cls}`}
                      role="menuitem"
                      onClick={closeAll}
                      data-testid={`nav-svc-${s.cls}`}
                    >
                      <span className="svc-item-ic">
                        <i className={`fa-solid ${s.icon}`}></i>
                      </span>
                      <span className="svc-item-tx">
                        <b>{s.title}</b>
                        <small>{s.sub}</small>
                      </span>
                      <i className="fa-solid fa-arrow-right svc-item-go"></i>
                    </Link>
                  ))}
                  <a href="#services" className="svc-menu-foot" onClick={closeAll}>
                    Learn more about both services ↓
                  </a>
                </div>
              </div>

              {NAV_POST.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={closeAll}
                  data-testid={`nav-link-${l.href.slice(1)}`}
                >
                  {l.label}
                </a>
              ))}

              {/* Login → choose-your-service section */}
              <a
                href="#services"
                className="nav-login-btn"
                onClick={closeAll}
                data-testid="nav-login-btn"
              >
                <i className="fa-solid fa-right-to-bracket"></i> Login
              </a>
            </>
          )}
        </div>

        <button
          className="mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`}></i>
        </button>
      </div>
    </nav>
  );
}
